import * as React from 'react';

import { Skeleton } from 'antd';
import { Row, Text } from 'farmhub-ui-core';

import { useAppSelector } from 'app/hooks';
import { NameText } from 'components';
import { selectProductUnit } from 'slice';
import { getOrderItemQuantityByStatus } from 'utils';
import {
  buildOrdinalColumn,
  formatMoney,
  genWholesaleUnit,
  getDummyArray,
  isEmptyString,
  isWholesaleOrder,
} from 'utils/function';

import { CTable } from './styles';

interface ExpandTableProps {
  order: any;
  orderItems: any;
  isLoading: boolean;
  columnsVisible?: {
    [column: string]: boolean;
  };
}

const ExpandTable = ({
  order,
  orderItems,
  isLoading,
  columnsVisible,
}: ExpandTableProps): JSX.Element => {
  const [newOrderItems, setNewOrderItems] = React.useState([]);
  const productUnit = useAppSelector(selectProductUnit);

  const genColumns = React.useMemo(
    () => [
      {
        width: 30,
        title: (
          <Text
            color="black"
            size="xxs"
            styles={{ whiteSpace: 'nowrap' }}
            weight="extraBold"
          >
            STT
          </Text>
        ),
        key: 'order',
        align: 'center',
        render: (_: any, record: any, index: number) => (
          <NameText color="black">{buildOrdinalColumn(index)}</NameText>
        ),
      },
      {
        title: (
          <Text color="black" weight="extraBold">
            Tên hàng hóa
          </Text>
        ),
        dataIndex: 'product',
        key: 'name',
        render: (product: any, record: any) => (
          <Text
            color="black"
            size="xxs"
            styles={{ width: '100%', display: 'block' }}
            weight="bold"
          >
            {product.name}
          </Text>
        ),
      },
      ...(columnsVisible?.SKUCode
        ? [
            {
              align: 'center',
              width: '10%',
              title: (
                <Text color="black" size="xxs" weight="extraBold">
                  Mã SKU
                </Text>
              ),
              dataIndex: 'product',
              key: 'sku',
              render: (product: any, record: any) => (
                <Text color="black" weight="bold">
                  {product.code}
                </Text>
              ),
            },
          ]
        : []),
      {
        align: 'center',
        width: '9%',
        title: (
          <Text color="black" size="xxs" weight="extraBold">
            ĐVT
          </Text>
        ),
        dataIndex: 'product',
        key: 'unit',
        render: (product: any, record: any) =>
          !isWholesaleOrder(order.type) ? (
            <Text
              color="black"
              size="xxs"
              textTransform="uppercase"
              weight="bold"
            >
              {genWholesaleUnit(
                {
                  unitPrefix:
                    productUnit.prefixObj[
                      record.supplierProductRetailUnit?.prefix
                    ],
                  unitWeight: record.supplierProductRetailUnit?.weight,
                  unitPostfix:
                    productUnit.postfixObj[
                      record.supplierProductRetailUnit?.postfix
                    ],
                },
                true
              )}
            </Text>
          ) : (
            <Text
              color="black"
              size="xxs"
              textTransform="uppercase"
              weight="bold"
            >
              {genWholesaleUnit(
                {
                  unitPrefix:
                    productUnit.prefixObj[
                      record.customerProductWholesaleUnit?.prefix
                        ? record.customerProductWholesaleUnit?.prefix
                        : record.supplierProductWholesaleUnit?.prefix
                    ],
                  unitWeight:
                    record.customerProductWholesaleUnit?.weight != null
                      ? record.customerProductWholesaleUnit?.weight
                      : record.supplierProductWholesaleUnit?.weight,
                  unitPostfix:
                    productUnit.postfixObj[
                      record.customerProductWholesaleUnit?.postfix
                        ? record.customerProductWholesaleUnit?.postfix
                        : record.supplierProductWholesaleUnit?.postfix
                    ],
                },
                true
              )}
            </Text>
          ),
      },
      {
        align: 'center',
        width: '8%',
        title: (
          <Text color="black" size="xxs" weight="extraBold">
            Số lượng
          </Text>
        ),
        dataIndex: 'quantity',
        key: 'quantity',
        render: (quantity: number) => (
          <Text
            color={quantity === 0 ? 'red' : 'black'}
            size="xxs"
            weight="bold"
          >
            {quantity}
          </Text>
        ),
      },
      ...(columnsVisible?.RetailPrice
        ? [
            {
              align: 'center',
              width: '11%',
              title: (
                <Text color="black" size="xxs" weight="extraBold">
                  Đơn giá lẻ
                </Text>
              ),
              dataIndex: 'product',
              key: 'retailPrice',
              render: (product: any, record: any) => (
                <Row justifyContent="flex-end">
                  <Text color="black" weight="bold">
                    {`${formatMoney(product.price)}đ`}
                  </Text>
                </Row>
              ),
            },
          ]
        : []),
      ...(columnsVisible?.RetailUnit
        ? [
            {
              align: 'center',
              width: '10%',
              title: (
                <Text color="black" size="xxs" weight="extraBold">
                  ĐVT lẻ
                </Text>
              ),
              dataIndex: 'product',
              key: 'retailUnit',
              render: (product: any, record: any) => (
                <Text
                  color="black"
                  size="xxs"
                  textTransform="uppercase"
                  weight="bold"
                >
                  {genWholesaleUnit(
                    {
                      unitPrefix:
                        productUnit.prefixObj[product.retailUnit?.prefix],
                      unitWeight: product.retailUnit?.weight,
                      unitPostfix:
                        productUnit.postfixObj[product.retailUnit?.postfix],
                    },
                    true
                  )}
                </Text>
              ),
            },
          ]
        : []),

      {
        width: '11%',
        align: 'right',
        title: (
          <Text color="black" size="xxs" weight="extraBold">
            Đơn giá mua
          </Text>
        ),
        key: 'price',
        render: (record: any) => (
          <Row justifyContent="flex-end">
            <Text color="black" weight="bold">
              {`${formatMoney(record.originPrice)}đ`}
            </Text>
          </Row>
        ),
      },
      {
        width: '11%',
        align: 'right',
        title: (
          <Text color="black" size="xxs" weight="extraBold">
            Tiền hàng
          </Text>
        ),
        key: 'originPrice',
        render: (record: any) => (
          <Row justifyContent="flex-end">
            <Text color="black" weight="bold">
              {`${formatMoney(record.originPrice * record.quantity)}đ` || ''}
            </Text>
          </Row>
        ),
      },
      ...(columnsVisible?.PromotionPrice
        ? [
            {
              width: '11%',
              align: 'right',
              title: (
                <Text color="black" size="xxs" weight="extraBold">
                  Tiền KM
                </Text>
              ),
              key: 'promotionPrice',
              render: (record: any) => (
                <Row justifyContent="flex-end">
                  <Text color="black" weight="bold">
                    {record.totalAdjustment > 0
                      ? `${formatMoney(record.totalAdjustment)}đ`
                      : ''}
                  </Text>
                </Row>
              ),
            },
          ]
        : []),
      ...(columnsVisible?.SupplierNote || columnsVisible?.CustomerNote
        ? [
            {
              width: '14%',
              align: 'left',
              title: (
                <Text color="black" size="xxs" weight="extraBold">
                  Ghi chú
                </Text>
              ),
              key: 'note',
              render: (record: any) => (
                <Text color="black" weight="bold">
                  {[
                    columnsVisible?.CustomerNote ? record.customerNote : '',
                    columnsVisible?.SupplierNote ? record.supplierNote : '',
                  ]
                    .filter((item: string) => !isEmptyString(item))
                    .join(', ')}
                </Text>
              ),
            },
          ]
        : []),
      ...(columnsVisible?.TotalPrice
        ? [
            {
              width: '12%',
              align: 'right',
              title: (
                <Text color="black" size="xxs" weight="extraBold">
                  Thành tiền
                </Text>
              ),
              key: 'totalPrice',
              render: (record: any) => (
                <Row justifyContent="flex-end">
                  <Text color="black" weight="bold">
                    {formatMoney(
                      record.quantity < 0
                        ? record.originPrice * record.quantity +
                            record.totalAdjustment
                        : record.originPrice * record.quantity -
                            record.totalAdjustment
                    )}
                  </Text>
                </Row>
              ),
            },
          ]
        : []),
    ],
    [productUnit, columnsVisible]
  );

  React.useEffect(() => {
    if (!isLoading && order) {
      setNewOrderItems(
        (orderItems ?? []).map((item: any) => {
          return {
            ...item,
            quantity: getOrderItemQuantityByStatus({
              orderItem: item,
              status: order.status,
              trackingStatus: order.trackingStatus,
            }),
          };
        })
      );
    }
  }, [isLoading, order, orderItems]);

  return (
    <CTable
      bordered
      columns={isLoading ? columnsSkeleton : genColumns}
      dataSource={isLoading ? getDummyArray(10) : newOrderItems}
      isLoading={isLoading}
      locale={{
        emptyText: <Text color="black">Không có sản phẩm trong đơn hàng</Text>,
      }}
      pagination={false}
      showHeader={false}
    />
  );
};

export default ExpandTable;

const columnsSkeleton: Array<any> = [
  {
    width: 25,
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '25px', alignItems: 'center' }}
      />
    ),
    key: 'order',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '25px', alignItems: 'center' }}
      />
    ),
  },
  {
    width: '24%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '160px', alignItems: 'center' }}
      />
    ),
    key: 'product',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '160px', alignItems: 'center' }}
      />
    ),
  },
  {
    align: 'center',
    width: '14%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'customerQuantity',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
  {
    align: 'center',
    width: '14%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'supplierQuantity',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
  {
    align: 'center',
    width: '14%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'supplierDeliveredQuantity',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
  {
    align: 'center',
    width: '14%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'customerReceivedQuantity',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
  {
    width: '20%',
    title: (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
    key: 'price',
    render: () => (
      <Skeleton.Input
        size="small"
        style={{ width: '100px', alignItems: 'center' }}
      />
    ),
  },
];
